import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Smartphone, Tv, Monitor, Tablet, Check, Copy, Download, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { FamilyMember, XtreamCredentialMint } from '@lib/streamFlowApi';
import { getApiBaseUrl } from '@lib/apiBase';

/**
 * Per-device onboarding wizard — generates everything a customer needs
 * to set up StreamFlow on a phone / tablet / Fire TV / Android TV /
 * Apple TV in a single page.
 *
 * Output is a bundle of:
 *   - Tailscale install link (per-platform)
 *   - Xtream credentials (URL / user / pass) for the IPTV app
 *   - A short-code-protected .m3u URL (for Fire TV Downloader sideload)
 *   - QR codes for everything copyable
 *   - Per-device instructions
 *
 * The wizard is a one-page form: pick member, pick device type, click
 * generate, get bundle. No multi-step navigation — TV remotes hate
 * back-buttons.
 */

type DeviceType = 'phone' | 'tablet' | 'firetv' | 'androidtv' | 'appletv' | 'computer';

interface DeviceConfig {
  label: string;
  icon: typeof Smartphone;
  tailscaleUrl: string;
  iptvAppName: string;
  iptvAppUrl?: string;
  instructions: string[];
}

const DEVICES: Record<DeviceType, DeviceConfig> = {
  phone: {
    label: 'Phone (iPhone / Android)',
    icon: Smartphone,
    tailscaleUrl: 'https://tailscale.com/download',
    iptvAppName: 'IPTV Smarters Pro / Tivimate',
    instructions: [
      'Install Tailscale (QR below) and log in to your gasweb.info tailnet.',
      'Install the IPTV app from your app store.',
      'In the IPTV app, choose "Add Xtream Codes login" and paste the URL/Username/Password below — or scan the .m3u QR if your app supports M3U import.',
      'Open any channel to start watching.',
    ],
  },
  tablet: {
    label: 'Tablet (iPad / Android)',
    icon: Tablet,
    tailscaleUrl: 'https://tailscale.com/download',
    iptvAppName: 'IPTV Smarters Pro / Tivimate',
    instructions: [
      'Same flow as phone — install Tailscale, install the IPTV app, paste the credentials.',
      'Tip: use the .m3u QR code as a faster paste-free import path.',
    ],
  },
  firetv: {
    label: 'Fire TV / Fire Stick',
    icon: Tv,
    tailscaleUrl: 'https://www.amazon.com/dp/B0CPZK1FX9',
    iptvAppName: 'Tivimate / OTT Navigator',
    instructions: [
      'Install Tailscale from the Amazon Appstore (search "Tailscale" or use the link).',
      'Open Tailscale → Login → log in to gasweb.info tailnet.',
      'Install Downloader from the Amazon Appstore (one-time setup).',
      'Open Downloader → enter the SHORT CODE shown below → it downloads the .m3u file.',
      'Open Tivimate (or your IPTV app) → "Add playlist" → "M3U Playlist" → point at the downloaded .m3u, OR paste the URL/user/pass directly as Xtream Codes.',
      'You\'re done — channels load automatically.',
    ],
  },
  androidtv: {
    label: 'Android TV (Shield / Chromecast / Sony)',
    icon: Tv,
    tailscaleUrl: 'https://play.google.com/store/apps/details?id=com.tailscale.ipn',
    iptvAppName: 'Tivimate / OTT Navigator',
    instructions: [
      'Install Tailscale from Play Store.',
      'Log in to gasweb.info tailnet.',
      'Install Tivimate (or OTT Navigator) from Play Store.',
      'Use the .m3u QR — Tivimate scans QR codes directly during "Add playlist".',
      'OR paste the URL/user/pass as an Xtream Codes login.',
    ],
  },
  appletv: {
    label: 'Apple TV',
    icon: Tv,
    tailscaleUrl: 'https://apps.apple.com/us/app/tailscale/id1470499037',
    iptvAppName: 'iPlayTV / Channels DVR',
    instructions: [
      'Install Tailscale from the tvOS App Store.',
      'Sign in via "Login with Browser" — use your phone to scan and approve.',
      'Install an IPTV player (iPlayTV, Channels DVR, etc.).',
      'Add an Xtream Codes account using the URL/user/pass below.',
    ],
  },
  computer: {
    label: 'Computer (macOS / Windows / Linux)',
    icon: Monitor,
    tailscaleUrl: 'https://tailscale.com/download',
    iptvAppName: 'VLC / IINA / mpv',
    instructions: [
      'Install Tailscale from tailscale.com/download.',
      'Log in to gasweb.info tailnet.',
      'For watching: open the .m3u URL in VLC ("Open Network Stream"), or hit the M3U URL in any HLS-compatible player.',
    ],
  },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [memberId, setMemberId] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType>('phone');
  const [bundle, setBundle] = useState<{
    cred: XtreamCredentialMint;
    m3uUrl: string;
    shortCode: string;
    shortUrl: string;
  } | null>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    streamFlowApi.listMembers().then((r) => {
      if (r.success) {
        setMembers(r.data || []);
        if (r.data?.length) setMemberId(r.data[0].id);
      }
    });
  }, []);

  const apiBase = getApiBaseUrl().replace(/\/api$/, '');

  const handleGenerate = async () => {
    if (!memberId) return;
    setGenerating(true);
    setBundle(null);
    try {
      const member = members.find(m => m.id === memberId);
      const credRes = await streamFlowApi.createXtreamCredential({
        member_id: memberId,
        label: `${DEVICES[deviceType].label}${member ? ` (${member.display_name})` : ''}`,
      });
      if (!credRes.success || !credRes.data) {
        toast.error(credRes.error?.message || 'Failed to mint credential');
        setGenerating(false);
        return;
      }
      const cred = credRes.data;
      const m3uUrl = `${apiBase}/xtream/get.php?username=${encodeURIComponent(cred.username)}&password=${encodeURIComponent(cred.password)}`;

      const codeRes = await streamFlowApi.createShortCode({
        target_url: m3uUrl,
        label: `m3u for ${cred.label || cred.username}`,
        ttl_days: 30,
      });
      if (!codeRes.success || !codeRes.data) {
        toast.error('Cred minted but short-code failed; full URL still works.');
      }

      setBundle({
        cred,
        m3uUrl,
        shortCode: codeRes.data?.code ?? '',
        shortUrl: codeRes.data?.short_url ?? m3uUrl,
      });
    } finally {
      setGenerating(false);
    }
  };

  const device = DEVICES[deviceType];

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </button>

      <div>
        <h1 className="text-2xl font-bold">Set up a new device</h1>
        <p className="text-sm text-gray-500 mt-1">
          Generates everything you need to install StreamFlow on a new TV / phone / computer in one go.
        </p>
      </div>

      {/* Step 1: pick member */}
      <section className="bg-white border rounded-lg p-5 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold">1</span>
          Who's this device for?
        </h2>
        {members.length === 0 ? (
          // The <select> can't host a clickable link, so when there are no
          // members we replace it with an actual CTA that routes to /members
          // instead of leaving dead "add one in /members first" text.
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-center">
            <p className="text-sm text-gray-600 mb-3">
              No family members yet — add one before generating a device bundle.
            </p>
            <button
              type="button"
              onClick={() => navigate('/members')}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Add a family member
            </button>
          </div>
        ) : (
          <select
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            {members.map(m => (
              <option key={m.id} value={m.id}>{m.display_name}</option>
            ))}
          </select>
        )}
      </section>

      {/* Step 2: pick device type */}
      <section className="bg-white border rounded-lg p-5 space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold">2</span>
          What kind of device?
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(Object.entries(DEVICES) as [DeviceType, DeviceConfig][]).map(([k, v]) => {
            const Icon = v.icon;
            const selected = deviceType === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setDeviceType(k)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border transition-colors ${
                  selected
                    ? 'border-slate-800 bg-slate-50 text-slate-900 font-medium'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-left">{v.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 3: generate */}
      <section className="bg-white border rounded-lg p-5">
        <button
          onClick={handleGenerate}
          disabled={generating || !memberId}
          className="w-full px-4 py-3 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {generating ? 'Generating bundle…' : 'Generate setup bundle'}
        </button>
      </section>

      {/* Bundle output */}
      {bundle && (
        <section className="bg-white border-2 border-blue-200 rounded-lg p-5 space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              The IPTV password is shown only once. Save it now or scan the QR
              codes from the device. If you lose it, generate a new one.
            </span>
          </div>

          {/* Tailscale install */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Step A — Install Tailscale on the device</h3>
            <p className="text-xs text-gray-500 mb-2">
              Tailscale gives the device access to the gasweb.info network so streams can route through your home machines instead of Azure.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="bg-white border rounded-lg p-2 inline-block self-start">
                <QRCodeSVG value={device.tailscaleUrl} size={120} />
              </div>
              <div className="flex-1 text-xs text-gray-600 space-y-1">
                <p>Scan with the device's camera, or open this URL in its browser:</p>
                <CopyRow value={device.tailscaleUrl} compact />
                {deviceType === 'firetv' && (
                  <p className="mt-2 text-amber-700">
                    ⓘ Fire TV: type the URL into Downloader instead, or search "Tailscale" in the Amazon Appstore.
                  </p>
                )}
                <a
                  href={device.tailscaleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline mt-1"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          {/* IPTV credentials */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Step B — Configure {device.iptvAppName}</h3>
            <p className="text-xs text-gray-500 mb-3">
              Either scan the .m3u QR (fastest) or paste the URL / username / password manually.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white border rounded-lg p-3">
                <p className="text-xs font-medium mb-2 text-gray-700">.m3u QR (Tivimate / OTT Navigator scan-import)</p>
                <div className="flex flex-col items-center">
                  <QRCodeSVG value={bundle.m3uUrl} size={150} />
                </div>
                <p className="text-[10px] text-gray-500 mt-2 text-center break-all font-mono">{bundle.m3uUrl}</p>
                {bundle.shortCode && (
                  <p className="text-[11px] text-gray-700 mt-2 text-center">
                    Fire TV Downloader code: <span className="font-mono font-bold text-base text-slate-900">{bundle.shortCode}</span>
                  </p>
                )}
                <a
                  href={bundle.m3uUrl}
                  download={`streamflow-${bundle.cred.username}.m3u`}
                  className="mt-2 w-full text-center inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-500"
                >
                  <Download className="w-3 h-3" />
                  Download .m3u
                </a>
              </div>

              <div className="bg-white border rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium mb-2 text-gray-700">Manual paste (Xtream Codes login)</p>
                {/* The /xtream suffix is required: our facade mounts there, so
                    Tivimate's auto-built /player_api.php?username=...&password=...
                    needs the prefix or it 404s on the bare host. */}
                <CopyRow label="URL"      value={`${apiBase}/xtream`} />
                <CopyRow label="Username" value={bundle.cred.username} />
                <CopyRow label="Password" value={bundle.cred.password} mono />
              </div>
            </div>
          </div>

          {/* Step-by-step */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Step C — Walk through on the device</h3>
            <ol className="list-decimal list-inside text-xs text-gray-700 space-y-1">
              {device.instructions.map((step, i) => (
                <li key={i} className="leading-relaxed">{step}</li>
              ))}
            </ol>
          </div>

          <div className="text-xs text-gray-500 pt-3 border-t">
            Trouble? Visit{' '}
            <button onClick={() => navigate('/members')} className="text-blue-600 hover:underline">
              the family members page
            </button>
            {' '}to see all credentials, view last-used timestamps, or revoke this device.
          </div>
        </section>
      )}
    </div>
  );
}

function CopyRow({ label, value, mono, compact }: { label?: string; value: string; mono?: boolean; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      {label && <span className={`text-xs text-gray-500 ${compact ? '' : 'w-20 flex-shrink-0'}`}>{label}</span>}
      <code className={`flex-1 text-xs bg-gray-100 px-2 py-1 rounded truncate ${mono ? 'font-mono' : ''}`}>{value}</code>
      <button
        onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="p-1.5 hover:bg-gray-100 rounded"
        aria-label="Copy"
      >
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
      </button>
    </div>
  );
}
